import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { FieldDefinition, FieldEntityType } from '@mayksel/types';
import type {
  CreateFieldDefinitionInput,
  UpdateFieldDefinitionInput,
  ReorderFieldDefinitionsInput,
} from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useFieldDefinitions(entityType?: FieldEntityType) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<FieldDefinition[]>({
    queryKey: ['field-definitions', workspaceId, entityType],
    queryFn: async () => {
      const token = await getToken();
      const qs = entityType ? `?entityType=${entityType}` : '';
      return api.get<FieldDefinition[]>(`/v1/field-definitions${qs}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useCreateFieldDefinition() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateFieldDefinitionInput) => {
      const token = await getToken();
      return api.post<FieldDefinition>('/v1/field-definitions', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field-definitions', workspaceId] });
    },
  });
}

export function useUpdateFieldDefinition() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateFieldDefinitionInput }) => {
      const token = await getToken();
      return api.patch<FieldDefinition>(`/v1/field-definitions/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field-definitions', workspaceId] });
    },
  });
}

export function useDeleteFieldDefinition() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<null>(`/v1/field-definitions/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field-definitions', workspaceId] });
    },
  });
}

export function useReorderFieldDefinitions() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReorderFieldDefinitionsInput) => {
      const token = await getToken();
      return api.post<FieldDefinition[]>('/v1/field-definitions/reorder', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field-definitions', workspaceId] });
    },
  });
}
